import { Box, Button, Card, Grid, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import AccountTreeRounded from "@mui/icons-material/AccountTreeRounded";
import FolderRounded from "@mui/icons-material/FolderRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";
import WorkspacesRounded from "@mui/icons-material/WorkspacesRounded";
import ListAltRounded from "@mui/icons-material/ListAltRounded";
import StraightenRounded from "@mui/icons-material/StraightenRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { useData } from "../contexts/DataContext/DataContextt";
import PageContainer from "../components/Forms/PageContainer";
import StatCard from "../components/Dashboard/StatCard";
import { entranceSx, STAGGER_MS } from "../components/Dashboard/animations";

const STATS = [
  {
    label: "Classes",
    key: "classes",
    icon: AccountTreeRounded,
    path: "/classnew",
    caption: "Product classification",
  },
  {
    label: "Groups",
    key: "groups",
    icon: FolderRounded,
    path: "/groups",
    caption: "Attribute groupings",
  },
  {
    label: "Features",
    key: "features",
    icon: TuneRounded,
    path: "/featuresnew",
    caption: "Attribute definitions",
  },
  {
    label: "Feature Groups",
    key: "featureGroups",
    icon: WorkspacesRounded,
    path: "/featuregroups",
    caption: "Features organized by group",
  },
  {
    label: "Values",
    key: "values",
    icon: ListAltRounded,
    path: "/valuesnew",
    caption: "Allowed attribute values",
  },
  {
    label: "Units",
    key: "units",
    icon: StraightenRounded,
    path: "/unitsnew",
    caption: "Units of measure",
  },
];

// Quick actions enter just after the last stat card.
const QUICK_ACTIONS_ENTRANCE = entranceSx(STATS.length * STAGGER_MS + 80);

const QUICK_ACTIONS = [
  { label: "New Class", path: "/classes/new" },
  { label: "New Group", path: "/groups/new" },
  { label: "New Feature", path: "/features/new" },
  { label: "New Feature Group", path: "/featuregroups/new" },
  { label: "New Value", path: "/values/new" },
  { label: "New Unit", path: "/units/new" },
];

const DashboardPage = () => {
  const data = useData();
  const { reload } = data;

  useEffect(() => {
    const preloadAll = async () => {
      await reload([
        "features",
        "groups",
        "featureGroups",
        "classes",
        "units",
        "values",
      ]);
    };

    preloadAll();
  }, []);

  return (
    <PageContainer title="Dashboard" breadcrumbs={[{ title: "Dashboard" }]}>
      <Box sx={{ bgcolor: "grey.50", borderRadius: 3, p: { xs: 1.5, sm: 2.5 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Overview of your attribute model
        </Typography>

        <Grid container spacing={2.5}>
          {STATS.map(({ key, ...stat }, index) => (
            <Grid key={key} size={{ xs: 12, sm: 6, lg: 4 }}>
              <StatCard {...stat} index={index} value={data[key]?.total} />
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mt: 4,
            mb: 1.5,
            ...QUICK_ACTIONS_ENTRANCE,
          }}
        >
          <Box
            aria-hidden
            sx={(theme) => ({
              width: 4,
              height: 20,
              mr: 1.25,
              borderRadius: 1,
              bgcolor: theme.palette.brand.orange,
            })}
          />
          <Typography variant="subtitle1" fontWeight={600}>
            Quick actions
          </Typography>
        </Box>
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            p: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            ...QUICK_ACTIONS_ENTRANCE,
          }}
        >
          {QUICK_ACTIONS.map(({ label, path }) => (
            <Button
              key={path}
              component={Link}
              to={path}
              variant="outlined"
              startIcon={<AddRounded sx={{ color: "brand.orange" }} />}
              sx={(theme) => ({
                color: theme.palette.brand.navy,
                borderColor: theme.palette.brand.navy,
                textTransform: "none",
                borderRadius: 2,
                "&:hover": {
                  bgcolor: alpha(theme.palette.brand.orange, 0.08),
                  borderColor: theme.palette.brand.orange,
                },
                "&.Mui-focusVisible": {
                  outline: `2px solid ${theme.palette.brand.orange}`,
                  outlineOffset: 2,
                },
              })}
            >
              {label}
            </Button>
          ))}
        </Card>
      </Box>
    </PageContainer>
  );
};

export default DashboardPage;
